import type { BrandConfigDetail, OnlineAgentsCheck } from '@/common/api/models.pleased';
import type { ConnectToAgent } from '@/common/bot/types/config';
import { ConnectOptionType, type ConnectOptionKind } from '@/common/bot/enum/ConnectOptionType';

export const SKIP_TO_LIVE_AGENT_WELCOME_MESSAGE_ID = 'skip-to-live-agent-welcome';

export const isSkipToLiveAgentFlow = (connectConfig: ConnectToAgent | null | undefined): boolean => {
  return connectConfig?.skipToLiveAgent === true;
};

export const isSkipToLiveAgentActive = (brandConfig: BrandConfigDetail | null | undefined): boolean => {
  return brandConfig?.brandConfig?.skipToLiveAgent === true;
};

export type SkipWelcomeSystemMessage = {
  id: typeof SKIP_TO_LIVE_AGENT_WELCOME_MESSAGE_ID;
  message: string;
};

export const hasSkipWelcomeMessage = (messages: ReadonlyArray<{ id?: string | number }>): boolean =>
  messages.some((message) => message.id === SKIP_TO_LIVE_AGENT_WELCOME_MESSAGE_ID);

export const buildSkipWelcomeSystemMessage = (brandConfig: BrandConfigDetail): SkipWelcomeSystemMessage | null => {
  if (!brandConfig.brandConfig.showWelcomeMessage || !brandConfig.brandConfig.welcomeMessage) {
    return null;
  }

  return {
    id: SKIP_TO_LIVE_AGENT_WELCOME_MESSAGE_ID,
    message: brandConfig.brandConfig.welcomeMessage,
  };
};

export const buildSkipToLiveAgentConnectConfig = (brandConfig: BrandConfigDetail): ConnectToAgent => {
  const config = brandConfig.brandConfig;

  return {
    connect: true,
    skipToLiveAgent: true,
    skipAiChat: true,
    loginRequired: false,
    showDescriptionBox: false,
    liveChat: config.skipToLiveAgentLiveChatEnabled === true,
    voiceChat: config.skipToLiveAgentVoiceChatEnabled === true,
    openRequest: config.skipToLiveAgentChatRequestEnabled === true,
  };
};

export const getSingleSkipConnectOption = (
  connectConfig: ConnectToAgent | null | undefined
): ConnectOptionKind | null => {
  if (!connectConfig) {
    return null;
  }

  const options: ConnectOptionKind[] = [];

  if (connectConfig.liveChat === true) {
    options.push(ConnectOptionType.LIVE_CHAT);
  }

  if (connectConfig.voiceChat === true) {
    options.push(ConnectOptionType.VOICE_CALL);
  }

  if (connectConfig.openRequest === true) {
    options.push(ConnectOptionType.OPEN_REQUEST);
  }

  return options.length === 1 ? options[0] : null;
};

export const resolveSingleSkipConnectOptionWithAgents = (
  connectConfig: ConnectToAgent | null | undefined,
  agentsOnline: OnlineAgentsCheck
): ConnectOptionKind | null => {
  const singleOption = getSingleSkipConnectOption(connectConfig);

  if (!singleOption) {
    return null;
  }

  if (singleOption === ConnectOptionType.OPEN_REQUEST) {
    return ConnectOptionType.OPEN_REQUEST;
  }

  if (singleOption === ConnectOptionType.LIVE_CHAT) {
    return agentsOnline.onlineChatAgentsCheck === false ? ConnectOptionType.OPEN_REQUEST : ConnectOptionType.LIVE_CHAT;
  }

  if (singleOption === ConnectOptionType.VOICE_CALL) {
    return agentsOnline.onlineCallAgentsCheck === false ? ConnectOptionType.OPEN_REQUEST : ConnectOptionType.VOICE_CALL;
  }

  return singleOption;
};
